import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles } from "lucide-react";
import PhoneFrame from "./PhoneFrame";
import LiveCounter from "./LiveCounter";
import { APP_SCREENSHOTS, HERO_IMAGE } from "../../lib/assets";
import { track } from "../../lib/tracking";

export const Hero = () => {
  const { t } = useTranslation();
  const audiences = t("hero.audiences", { returnObjects: true }) || [];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden bg-background pt-12 lg:pt-20"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 lg:grid-cols-12 lg:gap-12 lg:px-10 lg:pb-32">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-[hsl(var(--ink-soft))]">
            <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--sand))]" strokeWidth={1.8} />
            {t("hero.betaLiveTag")}
          </div>

          <div className="mt-3">
            <LiveCounter />
          </div>

          <h1
            data-testid="hero-title"
            className="mt-8 font-display text-5xl font-semibold leading-[1.02] tracking-tight text-[hsl(var(--ink))] text-balance sm:text-6xl lg:text-[5.4rem]"
          >
            {t("hero.titleBefore")}{" "}
            <em className="font-display italic text-[hsl(var(--sand-dark))]">
              {t("hero.titleEm")}
            </em>
            <br />
            {t("hero.titleAfter")}
          </h1>

          <p className="mt-7 max-w-xl text-lg leading-relaxed text-[hsl(var(--ink-soft))] sm:text-xl">
            {t("hero.intro")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href="#waitlist"
              data-testid="hero-download-btn"
              onClick={() => track("cta_click", { label: "hero_beta" })}
              className="group inline-flex items-center gap-2 rounded-full bg-[hsl(var(--forest))] px-7 py-4 text-sm font-medium text-[hsl(var(--background))] transition-all hover:-translate-y-0.5 hover:bg-[hsl(var(--forest-dark))]"
            >
              {t("common.betaTester")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.8} />
            </a>
            <a
              href="#pricing"
              data-testid="hero-start-free-btn"
              onClick={() => track("cta_click", { label: "hero_pricing" })}
              className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--ink))] px-7 py-4 text-sm font-medium text-[hsl(var(--ink))] transition-all hover:-translate-y-0.5 hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--background))]"
            >
              {t("common.viewPricing")}
            </a>
          </div>

          <p className="mt-5 text-sm text-[hsl(var(--sand-dark))]">
            <span className="font-medium">{t("hero.betaPerkBold")}</span> {t("hero.betaPerk")}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
            <span className="font-display text-base normal-case tracking-normal italic text-[hsl(var(--sand-dark))]">
              {t("hero.tagline")}
            </span>
            {audiences.map((a) => (
              <span key={a}>· {a}</span>
            ))}
          </div>
        </div>

        <div className="relative lg:col-span-5">
          <div className="relative">
            <div className="absolute -left-6 -top-6 h-72 w-72 rounded-full bg-[hsl(var(--sand))]/15 blur-3xl" />
            <div className="absolute -bottom-10 right-0 h-80 w-80 rounded-full bg-[hsl(var(--forest))]/10 blur-3xl" />

            <div className="relative overflow-hidden rounded-[2rem] border border-[hsl(var(--border))]">
              <img
                src={HERO_IMAGE}
                alt={t("hero.titleBefore") + " " + t("hero.titleEm")}
                className="h-[460px] w-full object-cover sm:h-[560px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>

            <div className="absolute -bottom-10 -left-6 hidden sm:block lg:-left-10">
              <PhoneFrame
                testId="hero-phone-mockup"
                src={APP_SCREENSHOTS[0].url}
                alt={APP_SCREENSHOTS[0].name}
                className="rotate-[-4deg] !max-w-[210px]"
              />
            </div>

            <div className="absolute -right-3 top-10 hidden rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]/95 px-4 py-3 backdrop-blur sm:block">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">
                {t("hero.equicoachToday")}
              </p>
              <p className="mt-1 font-display text-2xl font-semibold text-[hsl(var(--ink))]">
                {t("hero.equicoachScore")}
              </p>
              <p className="mt-1 text-[10px] text-[hsl(var(--ink-soft))]">
                {t("hero.equicoachStatus")}
              </p>
              <div className="mt-2 flex gap-0.5">
                {Array.from({ length: 14 }).map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 w-2 rounded-sm ${
                      i < 9
                        ? "bg-[hsl(var(--forest))]"
                        : i < 11
                        ? "bg-[hsl(var(--sand))]"
                        : "bg-[hsl(var(--sand-dark))]"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
